import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

interface LibraryVideo {
  id: string;
  title: string;
  status: string;
  durationSec: number | null;
  createdAt: number | null;
  thumbnailUrl: string | null;
  videoUrl: string | null;
  inSystem: boolean;
  source: 'generated' | 'heygen_import' | null;
  ourVideoId: string | null;
  publications: Array<{ platform: string; status: string }>;
}

interface PpProfile { id: string; name: string; language: string }

const PLATFORM_META: Record<string, { icon: string; label: string }> = {
  instagram:       { icon: '📸', label: 'Instagram' },
  facebook:        { icon: '👍', label: 'Facebook' },
  tiktok:          { icon: '🎵', label: 'TikTok' },
  youtube:         { icon: '▶️', label: 'YouTube' },
  linkedin:        { icon: '💼', label: 'LinkedIn' },
  'linkedin-page': { icon: '🏢', label: 'LinkedIn (страница)' },
  telegram:        { icon: '✈️', label: 'Telegram' },
};

const PUB_STATUS: Record<string, { label: string; cls: string }> = {
  queued:     { label: 'в очереди',  cls: 'badge-slate' },
  publishing: { label: 'публикуется', cls: 'badge-amber' },
  published:  { label: 'опубликовано', cls: 'badge-green' },
  failed:     { label: 'ошибка',     cls: 'badge-red' },
};

/** One selectable publication target: a PostProxy profile, or the native Telegram channel. */
interface Target { key: string; platform: string; profileId?: string; label: string }

function errMsg(err: unknown): string {
  return (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка';
}

function fmtDuration(sec: number | null): string {
  if (sec == null) return '';
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** value for <input type="datetime-local"> in local time, N minutes from now. */
function localInputValue(plusMinutes = 0): string {
  const d = new Date(Date.now() + plusMinutes * 60_000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function HeyGenLibrary() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [videos, setVideos] = useState<LibraryVideo[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [onlyNew, setOnlyNew] = useState(false);

  const [targets, setTargets] = useState<Target[]>([]);

  // Scheduling modal
  const [picked, setPicked] = useState<LibraryVideo | null>(null);
  const [pickedTargets, setPickedTargets] = useState<Set<string>>(new Set());
  const [when, setWhen] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtagsText, setHashtagsText] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function loadPage(token?: string) {
    const r = await api.get('/videos/heygen-library', { params: { limit: 24, ...(token ? { token } : {}) } });
    return r.data as { videos: LibraryVideo[]; nextToken: string | null };
  }

  async function reload() {
    setLoading(true);
    setLoadError(null);
    try {
      const page = await loadPage();
      setVideos(page.videos);
      setNextToken(page.nextToken);
    } catch (err) {
      setLoadError(errMsg(err));
    } finally { setLoading(false); }
  }

  async function loadMore() {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const page = await loadPage(nextToken);
      setVideos((prev) => [...prev, ...page.videos]);
      setNextToken(page.nextToken);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoadingMore(false); }
  }

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    reload();

    // Publication targets = the tenant's connected PostProxy profiles (+ native Telegram).
    Promise.all([
      api.get('/settings/social/postproxy/status').catch(() => ({ data: null })),
      api.get('/settings/social').catch(() => ({ data: { connections: [] } })),
    ]).then(([pp, social]) => {
      const list: Target[] = [];
      const profiles: Record<string, PpProfile[]> = pp.data?.profiles ?? {};
      for (const [platform, arr] of Object.entries(profiles)) {
        if (!PLATFORM_META[platform]) continue;
        for (const p of arr) {
          list.push({
            key: `${platform}:${p.id}`, platform, profileId: p.id,
            label: `${PLATFORM_META[platform].label} · ${p.name}${p.language !== 'unknown' ? ` (${p.language})` : ''}`,
          });
        }
      }
      const tg = (social.data?.connections ?? []).find((c: { platform: string; isActive: boolean }) => c.platform === 'telegram' && c.isActive);
      if (tg) list.push({ key: 'telegram', platform: 'telegram', label: `${PLATFORM_META.telegram!.label} · канал` });
      setTargets(list);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  function openSchedule(v: LibraryVideo) {
    setPicked(v);
    setPickedTargets(new Set(targets.map((t) => t.key))); // all connected channels on by default
    setWhen(localInputValue(0));
    setCaption('');
    setHashtagsText('');
    setSeoTitle(v.title && !/^[0-9a-f]{20,}$/i.test(v.title) ? v.title : '');
  }

  const scheduledInFuture = useMemo(
    () => !!when && new Date(when).getTime() > Date.now() + 60_000,
    [when]
  );

  async function submit() {
    if (!picked) return;
    const chosen = targets.filter((t) => pickedTargets.has(t.key));
    if (chosen.length === 0) { toast.error('Выберите минимум один канал'); return; }
    const hashtags = hashtagsText.split(/[\s,]+/).map((h) => h.replace(/^#/, '').trim()).filter(Boolean);
    setSubmitting(true);
    try {
      await api.post('/videos/heygen-library/schedule', {
        heygenVideoId: picked.id,
        title: picked.title || undefined,
        scheduledAt: new Date(when || Date.now()).toISOString(),
        targets: chosen.map((t) => ({ platform: t.platform, ...(t.profileId ? { profileId: t.profileId } : {}) })),
        caption,
        hashtags,
        ...(seoTitle.trim() ? { seoTitle: seoTitle.trim() } : {}),
      });
      toast.success(scheduledInFuture
        ? `Запланировано в ${chosen.length} каналов`
        : `Публикация запущена в ${chosen.length} каналов`);
      setPicked(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSubmitting(false); }
  }

  const shown = onlyNew ? videos.filter((v) => !v.inSystem) : videos;

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-6 pb-16">
        <header className="mb-6">
          <button onClick={() => navigate('/videos')} className="text-sm text-slate-500 hover:text-slate-700 mb-2">
            ← К видео
          </button>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Библиотека HeyGen</h1>
          <p className="text-slate-500 mt-1.5">
            Все видео вашего HeyGen-аккаунта — в том числе созданные прямо в HeyGen. Выберите видео и запланируйте публикацию в свои каналы.
          </p>
        </header>

        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} className="accent-indigo-600" />
            Только не использованные в сервисе
          </label>
          <button onClick={reload} disabled={loading} className="btn btn-sm btn-ghost ml-auto">🔄 Обновить</button>
        </div>

        {targets.length === 0 && !loading && (
          <div className="text-sm text-amber-800 bg-amber-50 rounded-xl p-3 mb-5">
            Нет подключённых каналов публикации. Откройте <a href="/settings" className="underline">Настройки → PostProxy</a> и подключите соцсети.
          </div>
        )}

        {loadError && (
          <div className="card-glass p-8 text-center">
            <div className="text-4xl mb-2">⚠️</div>
            <div className="font-semibold mb-1">Не удалось загрузить библиотеку</div>
            <div className="text-sm text-slate-500 mb-4">{loadError}</div>
            <button onClick={() => navigate('/settings')} className="btn btn-md btn-primary">⚙️ В настройки</button>
          </div>
        )}

        {loading && !loadError && <div className="text-slate-400 text-sm">Загружаю видео из HeyGen…</div>}

        {!loading && !loadError && shown.length === 0 && (
          <div className="card-glass p-12 text-center text-slate-500">В аккаунте HeyGen нет подходящих видео.</div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {shown.map((v) => {
            const ready = v.status === 'completed';
            return (
              <div key={v.id} className="card-glass card-glass-hover overflow-hidden flex flex-col">
                <div className="aspect-video bg-slate-900 relative">
                  {ready && v.videoUrl ? (
                    <video controls preload="none" poster={v.thumbnailUrl ?? undefined} className="w-full h-full object-contain" src={v.videoUrl} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                      {ready ? 'Нет превью' : `⏳ ${v.status}`}
                    </div>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="text-sm font-semibold truncate mb-1" title={v.title}>{v.title || v.id}</div>
                  <div className="flex items-center gap-2 flex-wrap text-xs text-slate-400 mb-2">
                    {v.createdAt && <span>{new Date(v.createdAt * 1000).toLocaleDateString('ru-RU')}</span>}
                    {v.durationSec != null && <span>{fmtDuration(v.durationSec)}</span>}
                    {v.inSystem && <span className="badge badge-violet">в сервисе</span>}
                  </div>
                  {v.publications.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {v.publications.map((p, i) => (
                        <span key={i} className={`badge ${PUB_STATUS[p.status]?.cls ?? 'badge-slate'}`}>
                          {PLATFORM_META[p.platform]?.icon ?? '•'} {PUB_STATUS[p.status]?.label ?? p.status}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-2">
                    <button onClick={() => openSchedule(v)} disabled={!ready || targets.length === 0} className="btn btn-sm btn-primary w-full">
                      📅 Запланировать
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {nextToken && !loading && (
          <div className="text-center mt-6">
            <button onClick={loadMore} disabled={loadingMore} className="btn btn-md btn-ghost">
              {loadingMore ? 'Загружаю…' : 'Показать ещё'}
            </button>
          </div>
        )}
      </div>

      {/* === Scheduling modal === */}
      {picked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4 overflow-y-auto py-6"
             onClick={() => setPicked(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card-glass max-w-lg w-full p-6 shadow-2xl">
            <h2 className="text-lg font-semibold mb-1">📅 Запланировать публикацию</h2>
            <div className="text-xs text-slate-500 mb-4 truncate">{picked.title || picked.id}</div>

            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">
                Каналы ({pickedTargets.size} из {targets.length})
              </div>
              <div className="grid gap-2 max-h-48 overflow-y-auto">
                {targets.map((t) => {
                  const checked = pickedTargets.has(t.key);
                  return (
                    <label key={t.key}
                           className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition ring-1 ${
                             checked ? 'bg-indigo-50 ring-indigo-300' : 'bg-white/50 ring-slate-200 hover:bg-slate-50'}`}>
                      <input type="checkbox" checked={checked} className="accent-indigo-600"
                             onChange={() => setPickedTargets((prev) => {
                               const n = new Set(prev);
                               if (n.has(t.key)) n.delete(t.key); else n.add(t.key);
                               return n;
                             })} />
                      <span className="text-lg">{PLATFORM_META[t.platform]?.icon}</span>
                      <span className="text-sm truncate">{t.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Подпись к посту</div>
              <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={4}
                        placeholder="Текст поста (подписи из скриптов у видео из библиотеки нет — впишите свой)"
                        className="input w-full" />
              <input value={hashtagsText} onChange={(e) => setHashtagsText(e.target.value)}
                     placeholder="Хэштеги через пробел или запятую" className="input w-full mt-2" />
              <input value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={100}
                     placeholder="Заголовок для YouTube (необязательно)" className="input w-full mt-2" />
            </div>

            <div className="mb-5">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Когда публиковать</div>
              <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="input w-full" />
              <div className="text-xs text-slate-500 mt-1">
                Видео скачивается в сервис заранее; в момент публикации при необходимости берётся свежая ссылка из HeyGen.
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={() => setPicked(null)} className="btn btn-md btn-ghost">Отмена</button>
              <button onClick={submit} disabled={submitting || pickedTargets.size === 0} className="btn btn-md btn-primary">
                {submitting ? '⏳ Отправляю…' : scheduledInFuture
                  ? `📅 Запланировать (${pickedTargets.size})`
                  : `🚀 Опубликовать сейчас (${pickedTargets.size})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
